<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onUnmounted, useAttrs } from 'vue';
import { X } from 'lucide-vue-next';
import { trapFocus } from '@/utils/focus-trap';
import BaseIcon from './BaseIcon.vue';

interface Props {
  isOpen?: boolean;
  title?: string;
  showCloseButton?: boolean;
  backdropDismiss?: boolean;
  canDismiss?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'full';
  ariaLabel?: string;
}

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{
  close: [];
  dismiss: [];
}>();

const props = withDefaults(defineProps<Props>(), {
  showCloseButton: true,
  backdropDismiss: true,
  canDismiss: true,
  size: 'md',
});

const attrs = useAttrs();
const modalRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const previousActiveElement = ref<HTMLElement | null>(null);
let focusCleanup: (() => void) | null = null;

const forwardedAttrs = computed(() => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(attrs)) {
    if (key !== 'data-testid') {
      result[key] = value;
    }
  }

  return result;
});

const modalTestId = computed(() => {
  const customTestId = attrs['data-testid'];

  if (typeof customTestId === 'string' && customTestId.trim().length > 0) {
    return customTestId;
  }

  return `base-modal${props.title ? `-${props.title.toLowerCase().replace(/\s+/g, '-')}` : ''}`;
});

// Handle backdrop click
const handleBackdropClick = () => {
  if (props.backdropDismiss && props.canDismiss) {
    emit('dismiss');
    emit('close');
  }
};

// Handle ESC key
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.canDismiss) {
    emit('dismiss');
    emit('close');
  }
};

// Focus management
const openModal = async () => {
  await nextTick();
  if (contentRef.value) {
    previousActiveElement.value = document.activeElement as HTMLElement;
    focusCleanup = trapFocus(contentRef.value);
  }
};

const closeModal = () => {
  if (focusCleanup) {
    focusCleanup();
    focusCleanup = null;
  }
  if (previousActiveElement.value) {
    previousActiveElement.value.focus();
    previousActiveElement.value = null;
  }
};

watch(() => props.isOpen, async (isOpen) => {
  if (isOpen) {
    await openModal();
  } else {
    closeModal();
  }
});

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  if (props.isOpen) {
    openModal();
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  closeModal();
});
</script>

<template>
  <!-- Backdrop -->
  <Teleport to="body">
    <Transition
      name="modal"
      appear
      @after-enter="() => {}"
    >
      <div
        v-if="isOpen"
        ref="modalRef"
        class="fixed inset-0 z-modal-backdrop bg-black/50 backdrop-blur-sm"
        @click.self="handleBackdropClick"
        :aria-hidden="!isOpen"
      >
        <!-- Modal container -->
        <Transition
          name="modal-content"
          appear
        >
          <div
            v-if="isOpen"
            ref="contentRef"
            class="fixed inset-0 z-modal flex items-center justify-center p-sm safe-top safe-bottom"
            role="dialog"
            :aria-modal="isOpen"
            :aria-label="ariaLabel"
            v-bind="forwardedAttrs"
            :data-testid="modalTestId"
          >
            <!-- Modal content -->
            <div
              class="bg-bg-secondary rounded-xl max-h-[90vh] overflow-hidden flex flex-col"
              style="box-shadow: var(--shadow-xl)"
              :class="{
                'w-full max-w-sm': size === 'sm',
                'w-full max-w-md': size === 'md',
                'w-full max-w-lg': size === 'lg',
                'w-full h-full max-w-full': size === 'full',
              }"
            >
              <!-- Header -->
              <div
                v-if="title || $slots.header"
                class="flex items-center justify-between px-md py-sm border-b border-border-light"
              >
                <div v-if="title" class="text-lg font-semibold text-text-primary">
                  {{ title }}
                </div>
                <slot name="header" />
                <button
                  v-if="showCloseButton"
                  @click="() => { emit('dismiss'); emit('close'); }"
                  class="text-text-secondary hover:text-text-primary transition-colors p-1"
                  :aria-label="'Close modal' + (title ? `: ${title}` : '')"
                  data-testid="modal-close-button"
                >
                  <BaseIcon :icon="X" size="lg" />
                </button>
              </div>

              <!-- Content -->
              <div class="flex-1 overflow-y-auto p-md">
                <slot />
              </div>

              <!-- Footer -->
              <div v-if="$slots.footer" class="px-md py-sm border-t border-border-light">
                <slot name="footer" />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Modal transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--transition-base);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Content slide up animation */
.modal-content-enter-active,
.modal-content-leave-active {
  transition: transform var(--transition-base), opacity var(--transition-base);
}

.modal-content-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.modal-content-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
